import { useEffect, useRef, useState } from 'react';
import { navItems, hotSearchWords, siteName } from '@/mocks/homeData';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  return (
    <header className="sticky top-0 z-40 w-full">
      <div className="border-b border-background-200/80 bg-background-50/90 backdrop-blur-md">
        <div className="flex h-16 items-center gap-3 px-4 md:px-6">
          <h1 className="shrink-0">
            <a href="/" className="flex items-center gap-2.5 cursor-pointer">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-black text-background-50">
                瓜
              </span>
              <span className="leading-none">
                <span className="block font-heading text-lg font-black tracking-tight text-foreground-950">
                  {siteName}
                </span>
                <span className="mt-0.5 block font-label text-[10px] tracking-[0.28em] text-foreground-500">
                  XIGUA&nbsp;HEILIAO
                </span>
              </span>
            </a>
          </h1>

          <nav className="ml-4 hidden items-center gap-0.5 lg:flex">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={`#${item.key}`}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-primary-100/70 hover:text-primary-800 cursor-pointer"
              >
                <i className={`${item.icon} text-base`}></i>
                {item.label}
              </a>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div ref={searchRef} className="relative hidden sm:block">
              <div className="flex h-10 w-56 items-center gap-2 rounded-md border border-background-300/70 bg-background-100/80 px-3 focus-within:border-primary-400 lg:w-64">
                <i className="ri-search-line text-base text-foreground-500"></i>
                <input
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="搜索爆料 / 合集 / 热帖"
                  className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                />
              </div>
              {searchOpen && (
                <div className="absolute right-0 top-12 w-72 rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="mb-2 font-label text-xs text-foreground-500">正在被围观的热搜词</p>
                  <div className="flex flex-wrap gap-1.5">
                    {hotSearchWords.map((word) => (
                      <button
                        key={word}
                        type="button"
                        onClick={() => {
                          setSearchValue(word);
                          setSearchOpen(false);
                        }}
                        className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setNotice('投稿通道正在整理中，先把材料收好，稍后就能提交。')}
              className="hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 md:block cursor-pointer"
            >
              我要投稿
            </button>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-800 lg:hidden cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200/80 bg-background-50 px-4 py-3 lg:hidden">
            <div className="mb-3 flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100/80 px-3 py-2">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="搜索爆料 / 合集 / 热帖"
                className="w-full bg-transparent font-label text-sm outline-none placeholder:text-foreground-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={`#${item.key}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800 cursor-pointer"
                >
                  <i className={`${item.icon} text-base text-primary-600`}></i>
                  {item.label}
                </a>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setNotice('投稿通道正在整理中，先把材料收好，稍后就能提交。');
                setMenuOpen(false);
              }}
              className="mt-3 w-full whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 cursor-pointer"
            >
              我要投稿
            </button>
          </div>
        )}
      </div>

      {notice && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-50 flex justify-center px-4">
          <div className="animate-fade-up rounded-full border border-foreground-800 bg-foreground-950 px-5 py-2.5 font-label text-sm text-background-50">
            {notice}
          </div>
        </div>
      )}
    </header>
  );
}